import { useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { FileText, Target, Loader2, ArrowLeft, Crown, Lock, TrendingUp, Check, X, ArrowRight } from 'lucide-react';
import { ThemeToggle } from '@/components/ThemeToggle';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function ScoreBar({ label, score, maxScore = 100 }) {
  const pct = Math.min((score / maxScore) * 100, 100);
  let color = '#EF4444';
  if (score >= 70) color = '#10B981';
  else if (score >= 40) color = '#F59E0B';
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="text-slate-600 dark:text-slate-400">{label}</span>
        <span className="font-bold" style={{ color }}>{score}</span>
      </div>
      <div className="w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}

function ScoreComparison({ before, after, improvement }) {
  return (
    <div className="grid grid-cols-3 gap-4 text-center" data-testid="score-comparison">
      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-4">
        <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Before</p>
        <p className="text-3xl font-black text-slate-900 dark:text-white">{before}</p>
      </div>
      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-4 flex flex-col items-center justify-center">
        <TrendingUp className="w-5 h-5 text-emerald-500 mb-1" />
        <p className="text-xl font-black text-emerald-500">+{improvement}</p>
      </div>
      <div className="bg-[#FFEDD5] dark:bg-orange-900 border border-[#FB923C] p-4">
        <p className="text-xs text-slate-600 dark:text-orange-300 mb-1">After</p>
        <p className="text-3xl font-black text-slate-900 dark:text-orange-100">{after}</p>
      </div>
    </div>
  );
}

function KeywordPills({ title, keywords, variant }) {
  if (!keywords || keywords.length === 0) return null;
  const COLORS = {
    match: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800',
    missing: 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800',
  };
  const colors = COLORS[variant] || 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800';

  return (
    <div className="space-y-2">
      <h4 className="text-sm font-bold text-slate-900 dark:text-white">{title}</h4>
      <div className="flex flex-wrap gap-1.5">
        {keywords.slice(0, 20).map((kw) => (
          <span key={kw} className={`text-xs px-2 py-0.5 border rounded ${colors}`}>{kw}</span>
        ))}
      </div>
    </div>
  );
}

function ChangesList({ changes }) {
  if (!changes || changes.length === 0) return null;
  return (
    <div className="space-y-2" data-testid="changes-list">
      <h4 className="text-sm font-bold text-slate-900 dark:text-white">Changes Made</h4>
      <ul className="space-y-1.5">
        {changes.map((change, idx) => (
          <li key={`change-${idx}`} className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300">
            <Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
            <span>{change}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function PaywallBanner({ onUpgrade }) {
  return (
    <div className="bg-slate-900 dark:bg-slate-800 border border-slate-700 p-6 text-center" data-testid="paywall-banner">
      <Lock className="w-8 h-8 text-[#FB923C] mx-auto mb-3" />
      <h3 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Upgrade to unlock optimization</h3>
      <p className="text-sm text-slate-400 mb-4">See your optimized resume, after score, and detailed changes with Pro or Plus</p>
      <Button data-testid="upgrade-btn" onClick={onUpgrade} className="bg-[#FB923C] hover:bg-orange-300 text-slate-900 font-bold rounded-none px-6 py-3 transition-all">
        <Crown className="w-4 h-4 mr-2" /> View Plans
      </Button>
    </div>
  );
}

export default function ATSOptimizerPage() {
  const { id: resumeId } = useParams();
  const navigate = useNavigate();
  const { isPro } = useSubscription();
  const [jobDescription, setJobDescription] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);

  const handleAnalyze = useCallback(async () => {
    if (!jobDescription.trim()) { toast.error('Paste a job description first'); return; }
    setAnalyzing(true);
    setResult(null);
    try {
      let resumeData;
      if (resumeId) {
        const { data: resume } = await axios.get(`${API_URL}/api/resumes/${resumeId}`, { withCredentials: true });
        resumeData = resume.data;
      } else {
        toast.error('No resume selected'); setAnalyzing(false); return;
      }
      const { data } = await axios.post(`${API_URL}/api/ats/full-analysis`, {
        resume_data: resumeData,
        job_description: jobDescription
      }, { withCredentials: true });
      setResult(data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  }, [jobDescription, resumeId]);

  const handleApplyOptimization = useCallback(async () => {
    if (!result?.optimized_resume || !resumeId) return;
    try {
      await axios.put(`${API_URL}/api/resumes/${resumeId}`, {
        data: result.optimized_resume
      }, { withCredentials: true });
      toast.success('Optimized resume applied!');
      setApplyDialogOpen(false);
      navigate(`/builder/${resumeId}`);
    } catch (error) {
      toast.error('Failed to apply optimization');
    }
  }, [result, resumeId, navigate]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
        <div className="max-w-5xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <FileText className="w-8 h-8 text-[#FB923C]" strokeWidth={2} />
            <span className="text-2xl font-black text-slate-900 dark:text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Aarova AI</span>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <Button data-testid="back-builder-btn" onClick={() => navigate(resumeId ? `/builder/${resumeId}` : '/dashboard')} className="bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200 rounded-none px-4 py-2 text-sm">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-[#FFEDD5] dark:bg-orange-900 border border-[#FB923C] px-4 py-1.5 text-sm font-bold text-slate-900 dark:text-orange-100 mb-4">
            <Target className="w-4 h-4" /> ATS OPTIMIZER
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-slate-900 dark:text-white mb-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }} data-testid="ats-optimizer-heading">
            Optimize for ATS
          </h1>
          <p className="text-base text-slate-600 dark:text-slate-400 max-w-2xl mx-auto" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            Paste a job description and AI will analyze your resume, find missing keywords, and optimize it automatically
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Left: Input */}
          <div className="space-y-4">
            <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-6">
              <h3 className="text-sm font-bold text-slate-900 dark:text-white mb-3">Job Description</h3>
              <Textarea
                data-testid="jd-input"
                value={jobDescription}
                onChange={(e) => setJobDescription(e.target.value)}
                rows={12}
                placeholder="Paste the full job description here..."
                className="rounded-none border-slate-200 dark:border-slate-600 dark:bg-slate-900 text-sm"
              />
            </div>
            <Button
              data-testid="analyze-btn"
              onClick={handleAnalyze}
              disabled={analyzing || !jobDescription.trim()}
              className="w-full bg-[#FB923C] hover:bg-orange-300 text-slate-900 font-bold rounded-none px-6 py-4 text-lg transition-all hover:-translate-y-1 shadow-[4px_4px_0px_#0F172A] disabled:opacity-50"
            >
              {analyzing ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Target className="w-5 h-5 mr-2" />}
              {analyzing ? 'Analyzing...' : 'Analyze & Optimize'}
            </Button>
          </div>

          {/* Right: Results */}
          <div className="space-y-4">
            {!result && !analyzing && (
              <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-12 text-center">
                <Target className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                <p className="text-slate-500 dark:text-slate-400">Results will appear here after analysis</p>
              </div>
            )}

            {analyzing && (
              <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-12 text-center">
                <Loader2 className="w-10 h-10 animate-spin text-[#FB923C] mx-auto mb-4" />
                <p className="text-slate-700 dark:text-slate-300 font-semibold">AI is analyzing your resume...</p>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">This may take 10-20 seconds</p>
              </div>
            )}

            {result && (
              <div className="space-y-4" data-testid="analysis-results">
                {/* Score */}
                <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-6">
                  <h3 className="text-sm font-bold text-slate-900 dark:text-white mb-4">ATS Score</h3>
                  <ScoreBar label="Before" score={result.before_score} />
                  {result.after_score != null && (
                    <>
                      <div className="my-3" />
                      <ScoreBar label="After Optimization" score={result.after_score} />
                      <div className="mt-4">
                        <ScoreComparison before={result.before_score} after={result.after_score} improvement={result.improvement} />
                      </div>
                    </>
                  )}
                  <p className="text-xs text-slate-400 mt-3">{result.note}</p>
                </div>

                {/* Keywords */}
                <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-6 space-y-4">
                  <KeywordPills title="Matching Keywords" keywords={result.matching_keywords} variant="match" />
                  <KeywordPills title="Missing Keywords" keywords={result.missing_keywords} variant="missing" />
                  {result.added_keywords && <KeywordPills title="Keywords Added by AI" keywords={result.added_keywords} variant="added" />}
                </div>

                {/* Changes (Pro only) */}
                {result.changes ? (
                  <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-6">
                    <ChangesList changes={result.changes} />
                  </div>
                ) : (
                  <PaywallBanner onUpgrade={() => navigate('/subscription')} />
                )}

                {/* Apply button (Pro only) */}
                {result.optimized_resume && (
                  <Button
                    data-testid="apply-optimization-btn"
                    onClick={() => setApplyDialogOpen(true)}
                    className="w-full bg-slate-900 dark:bg-white hover:bg-slate-800 dark:hover:bg-slate-100 text-white dark:text-slate-900 font-bold rounded-none px-6 py-4 text-lg"
                  >
                    Apply Optimized Resume <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      <Dialog open={applyDialogOpen} onOpenChange={setApplyDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Apply Optimized Resume?</DialogTitle>
            <DialogDescription>This will update your resume with the AI-optimized content. Your current data will be replaced.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-4">
            <Button onClick={() => setApplyDialogOpen(false)} className="flex-1 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200 rounded-none">Cancel</Button>
            <Button data-testid="confirm-apply-btn" onClick={handleApplyOptimization} className="flex-1 bg-[#FB923C] hover:bg-orange-300 text-slate-900 font-bold rounded-none">Apply Changes</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
